import { Skeleton } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { PropsWithChildren } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector } from '@verantos-dev/app-state-base';
import {
  selectBaselinePeriodRestrictionDaysBeforeIndexDate,
  selectBaselinePeriodRestrictionIsEnabled,
  selectFollowUpPeriodRestrictionDaysAfterIndexDate,
  selectFollowUpPeriodRestrictionIsEnabled,
  selectModal,
  selectOverallStudyPeriodRestrictionEndDate,
  selectOverallStudyPeriodRestrictionIsEnabled,
  selectOverallStudyPeriodRestrictionStartDate,
  setModal,
  useCohortEditable,
} from '@verantos-dev/app-state-cohort-builder';
import { selectCohortDefinitionIsVersionLoading } from '@verantos-dev/app-state-cohort-definition';
import { useCohortBuilderData } from '@verantos-dev/data-access-cohorts';
import { CohortBuilderModalLegacy, CohortStatusLegacy } from '@verantos-dev/types-cohorts';

import { CohortBuilderInfoCard } from '../../../../components/CohortBuilderInfoCard/CohortBuilderInfoCard';
import { CohortSection } from '../../../../components/CohortSection/CohortSection';
import { useCohortBuilderModalContext } from '../../../../contexts/CohortBuilderModalContext';
import { StudyPeriodEditCard } from '../../cohort-builder-edit-cards/StudyPeriodEditCard/StudyPeriodEditCard';

dayjs.extend(utc);

type StudyPeriodSectionProps = {
  minimalMode?: boolean;
};

export const StudyPeriodSection = ({ minimalMode }: StudyPeriodSectionProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector(selectCohortDefinitionIsVersionLoading);
  const { data: cohortData } = useCohortBuilderData();
  const isProcessing =
    !!cohortData?.data.latestStatus &&
    [CohortStatusLegacy.PROCESSING, CohortStatusLegacy.FINAL].includes(
      cohortData?.data.latestStatus
    );
  const { isEditable } = useCohortEditable();
  const viewMode = !isEditable || isProcessing;
  const modal = useAppSelector(selectModal);
  const { activeDialog } = useCohortBuilderModalContext();

  const isStudyPeriodRestrictionEnabled = useAppSelector(
    selectOverallStudyPeriodRestrictionIsEnabled
  );
  const studyPeriodRestrictionStartDate = useAppSelector(
    selectOverallStudyPeriodRestrictionStartDate
  );
  const studyPeriodRestrictionEndDate = useAppSelector(selectOverallStudyPeriodRestrictionEndDate);
  const studyPeriodValue = isStudyPeriodRestrictionEnabled
    ? `${
        studyPeriodRestrictionStartDate
          ? dayjs.utc(studyPeriodRestrictionStartDate).format('MM/DD/YYYY') // the date should be displayed as UTC
          : '--'
      }\u2013${
        studyPeriodRestrictionEndDate
          ? dayjs.utc(studyPeriodRestrictionEndDate).format('MM/DD/YYYY') // the date should be displayed as UTC
          : '--'
      }`
    : '--';

  const isBaselinePeriodRestrictionEnabled = useAppSelector(
    selectBaselinePeriodRestrictionIsEnabled
  );
  const daysBeforeIndexDate = useAppSelector(selectBaselinePeriodRestrictionDaysBeforeIndexDate);
  const baselinePeriodValue = isBaselinePeriodRestrictionEnabled
    ? intl.formatMessage(
        { id: 'projects.cohortBuilderPage.studyPeriodViewCard.baselineValue' },
        { days: daysBeforeIndexDate }
      )
    : '--';

  const isFollowUpPeriodRestrictionEnabled = useAppSelector(
    selectFollowUpPeriodRestrictionIsEnabled
  );
  const daysAfterIndexDate = useAppSelector(selectFollowUpPeriodRestrictionDaysAfterIndexDate);
  const followUpPeriodValue = isFollowUpPeriodRestrictionEnabled
    ? intl.formatMessage(
        { id: 'projects.cohortBuilderPage.studyPeriodViewCard.followUpValue' },
        { days: daysAfterIndexDate }
      )
    : '--';

  const showStudyPeriodEditModal = modal === CohortBuilderModalLegacy.CONFIGURE_STUDY_PERIOD;

  const onEditClick = isEditable
    ? () => {
        const isDisabled = activeDialog !== null || modal !== undefined;
        !isDisabled &&
          dispatch(
            setModal({
              id: 'configure-study-period',
              modal: CohortBuilderModalLegacy.CONFIGURE_STUDY_PERIOD,
            })
          );
      }
    : undefined;

  const fields = [
    {
      label: intl.formatMessage({
        id: 'projects.cohortBuilderPage.studyPeriodViewCard.studyPeriodLabel',
      }),
      value: studyPeriodValue,
    },
    {
      label: intl.formatMessage({
        id: 'projects.cohortBuilderPage.studyPeriodViewCard.baselinePeriodLabel',
      }),
      value: baselinePeriodValue,
    },
    {
      label: intl.formatMessage({
        id: 'projects.cohortBuilderPage.studyPeriodViewCard.followUpPeriodLabel',
      }),
      value: followUpPeriodValue,
    },
  ];

  const headerProps = {
    title: intl.formatMessage({ id: 'projects.cohortBuilderPage.studyPeriodSection.title' }),
  };

  const Wrapper = ({ children }: PropsWithChildren) =>
    minimalMode ? (
      children
    ) : (
      <CohortSection key={'study-period-section'} headerProps={headerProps}>
        {children}
      </CohortSection>
    );

  if (isLoading) {
    return (
      <Wrapper>
        <Skeleton variant="rectangular" height={78} />
      </Wrapper>
    );
  }

  return (
    <>
      <Wrapper>
        <CohortBuilderInfoCard
          id={'cohorts-builder-study-period-info-card'}
          info={fields}
          onEditClick={onEditClick}
          editTooltip={intl.formatMessage({
            id: 'projects.cohortBuilderPage.studyPeriodSection.editCardTitle',
          })}
          viewMode={viewMode}
          hideEdit={viewMode}
          minimalMode={minimalMode}
        />
      </Wrapper>

      {showStudyPeriodEditModal && <StudyPeriodEditCard />}
    </>
  );
};
